import type { FC, HtmlHTMLAttributes } from "react";

import * as stylex from "@stylexjs/stylex";

import { type OmitStyles, type QueryXsMax, colors } from "./../../../styles/tokens.stylex";

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const fontStyles = stylex.create({
    h1: {
        color: colors.fg_Primary,
        fontSize: { default: "4rem", [xsMax]: "2.25rem" },
        lineHeight: { default: "4.625rem", [xsMax]: "2.625rem" },
    },

    h2: {
        fontSize: { default: "3.375rem", [xsMax]: "1.875rem" },
        lineHeight: { default: "3.875rem", [xsMax]: "2.375rem" },
    },

    h3: {
        fontSize: { default: "1.75rem", [xsMax]: "1.25rem" },
        lineHeight: { default: "2.25rem", [xsMax]: "1.75rem" },
    },

    h4: {
        fontSize: { default: "1.5rem", [xsMax]: "1.125rem" },
        lineHeight: { default: "2rem", [xsMax]: "1.625rem" },
    },

    h5: {
        fontSize: { default: "1rem", [xsMax]: "0.875rem" },
        lineHeight: 1.25,
    },

    h6: {
        fontSize: { default: "1rem", [xsMax]: "0.875rem" },
        lineHeight: 1.25,
    },

    p: {
        fontSize: "1rem",
        lineHeight: 1.25,
    },

    primaryText: {
        color: colors.fg_Primary,
        fontSize: "1rem",
        fontWeight: 400,
        margin: 0,
        padding: 0,
        textTransform: "inherit",
    },
});

const weightStyles = stylex.create({
    bold: {
        fontWeight: 700,
    },
    "400": {
        fontWeight: 400,
    },
    "300": {
        fontWeight: 300,
    },
    unset: {
        fontWeight: null,
    },
});

export type IText = OmitStyles<HtmlHTMLAttributes<HTMLElement>> & {
    textElement: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
    fontStyle?: keyof typeof fontStyles;
    fontWeight?: keyof typeof weightStyles;
    className?: string;
};

export const Text: FC<IText> = ({
    textElement,
    fontStyle = "p",
    fontWeight = "unset",
    styleX,
    children,
    className,
    ...props
}) => {
    const Element = textElement;
    const styles = stylex.props(
        fontStyles.primaryText,
        fontStyles[fontStyle],
        weightStyles[fontWeight],
        styleX,
    );

    return (
        <Element {...props} className={styles.className + " " + (className || "")} style={styles.style}>
            {children}
        </Element>
    );
};
